// ============================================================================
// PLAYLISTS DIALOG — publishers create, edit, restrict and delete Studio TV
// playlists. A playlist with films, and the default playlist, cannot be deleted.
// ============================================================================

import React, { useState } from 'react';
import { AlertCircle, Check, Library, Loader, Lock, Pencil, Plus, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  StudioTvPlaylist,
  audienceLabel,
  createStudioTvPlaylist,
  deleteStudioTvPlaylist,
  updateStudioTvPlaylist
} from '../../services/studio-tv';
import AudienceDialog from './AudienceDialog';
import { ConfirmModal } from '../common/ConfirmModal';

interface Props {
  playlists: StudioTvPlaylist[];
  onClose: () => void;
  onCreated: (playlist: StudioTvPlaylist) => void;
  onUpdated: (playlist: StudioTvPlaylist) => void;
  onDeleted: (id: string) => void;
}

interface Draft {
  id: string | null; // null: new playlist
  title: string;
  description: string;
  order: 'newest' | 'manual';
}

const NEW_DRAFT: Draft = { id: null, title: '', description: '', order: 'newest' };
const inputCls = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:outline-none focus:border-primary';
const ghostBtn = 'flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors';
const primaryBtn = 'flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-60';

export default function PlaylistsDialog({ playlists, onClose, onCreated, onUpdated, onDeleted }: Props) {
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [audienceFor, setAudienceFor] = useState<StudioTvPlaylist | null>(null);
  const [deleting, setDeleting] = useState<StudioTvPlaylist | null>(null);

  const message = (err: any, fallback: string) => err?.response?.data?.error || fallback;

  async function saveDraft() {
    if (!draft || !draft.title.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const body = { title: draft.title.trim(), description: draft.description.trim(), order: draft.order };
      if (draft.id) onUpdated(await updateStudioTvPlaylist(draft.id, body));
      else onCreated(await createStudioTvPlaylist(body));
      setDraft(null);
    } catch (err) {
      setError(message(err, 'Failed to save the playlist'));
    } finally {
      setSaving(false);
    }
  }

  async function confirmDelete() {
    if (!deleting) return;
    setSaving(true);
    setError(null);
    try {
      await deleteStudioTvPlaylist(deleting.id);
      onDeleted(deleting.id);
      toast.success('Playlist deleted');
    } catch (err) {
      setError(message(err, 'Failed to delete the playlist'));
    } finally {
      setDeleting(null);
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/40 p-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden shadow-xl font-display flex flex-col max-h-[85vh]"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#e2e8f0]">
          <div>
            <h2 className="text-lg font-bold text-navy">Playlists</h2>
            <p className="text-xs text-slate-500">Group films, set their order and choose who can watch.</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><X size={16} /></button>
        </div>

        {error && (
          <div className="px-5 py-4 border-b border-[#e2e8f0] bg-red-50 flex gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm font-medium text-red-700">{error}</p>
          </div>
        )}

        <div className="p-5 flex flex-col gap-2 overflow-y-auto">
          {playlists.map(p => (
            <div key={p.id} className="flex items-center gap-3 p-3 rounded-xl border border-[#e2e8f0]">
              <div className="w-8 h-8 rounded-lg bg-slate-100 text-slate-400 flex items-center justify-center flex-shrink-0">
                <Library size={14} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-navy truncate">
                  {p.title}{p.isDefault && <span className="ml-2 text-[11px] font-semibold text-slate-400">Default</span>}
                </p>
                <p className="text-xs text-slate-500 flex items-center gap-2">
                  <span>{p.videoCount ?? 0} {p.videoCount === 1 ? 'film' : 'films'}</span>
                  <span>·</span>
                  <span>{p.order === 'manual' ? 'Manual order' : 'Newest first'}</span>
                  {audienceLabel(p.audience) && (
                    <span className="flex items-center gap-1 text-amber-700 truncate"><Lock size={11} />{audienceLabel(p.audience)}</span>
                  )}
                </p>
              </div>
              <button onClick={() => setAudienceFor(p)} title="Audience" className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><Lock size={15} /></button>
              <button onClick={() => { setError(null); setDraft({ id: p.id, title: p.title, description: p.description, order: p.order }); }}
                title="Edit" className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><Pencil size={15} /></button>
              <button onClick={() => setDeleting(p)} title="Delete" disabled={p.isDefault}
                className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-red-600 disabled:opacity-30 disabled:hover:text-slate-500"><Trash2 size={15} /></button>
            </div>
          ))}

          {draft ? (
            <div className="flex flex-col gap-3 p-3 rounded-xl border border-primary bg-primary/5">
              <input value={draft.title} onChange={e => setDraft({ ...draft, title: e.target.value })}
                placeholder="Title" maxLength={80} className={inputCls} autoFocus />
              <textarea value={draft.description} onChange={e => setDraft({ ...draft, description: e.target.value })}
                placeholder="Description (optional)" rows={2} maxLength={500} className={inputCls} />
              <div className="flex rounded-xl border border-slate-200 bg-white p-0.5 w-fit">
                {([['newest', 'Newest first'], ['manual', 'Manual order']] as const).map(([value, label]) => (
                  <button key={value} type="button" onClick={() => setDraft({ ...draft, order: value })}
                    className={`rounded-lg px-3 py-1.5 text-xs font-bold transition-colors ${
                      draft.order === value ? 'bg-primary text-white' : 'text-slate-600 hover:bg-slate-50'}`}>
                    {label}
                  </button>
                ))}
              </div>
              <div className="flex justify-end gap-3">
                <button onClick={() => setDraft(null)} className={ghostBtn}>Cancel</button>
                <button onClick={saveDraft} disabled={saving || !draft.title.trim()} className={primaryBtn}>
                  {saving ? <Loader size={16} className="animate-spin" /> : <Check size={16} />} {draft.id ? 'Save' : 'Create'}
                </button>
              </div>
            </div>
          ) : (
            <button onClick={() => { setError(null); setDraft(NEW_DRAFT); }}
              className="flex items-center justify-center gap-2 p-3 rounded-xl border border-dashed border-slate-300 text-sm font-bold text-slate-500 hover:border-primary hover:text-primary transition-colors">
              <Plus size={16} /> New playlist
            </button>
          )}
        </div>
      </div>

      {audienceFor && (
        <AudienceDialog initial={audienceFor.audience} subject="playlist" onClose={() => setAudienceFor(null)}
          onSave={async ids => {
            onUpdated(await updateStudioTvPlaylist(audienceFor.id, { audienceRoleIds: ids }));
            setAudienceFor(null);
          }} />
      )}

      <ConfirmModal
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        title="Delete this playlist?"
        message={`"${deleting?.title || ''}" will be removed. Playlists that still hold films cannot be deleted: move the films first.`}
        confirmText="Delete"
        variant="danger"
        isProcessing={saving}
      />
    </div>
  );
}
